import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 职业技能 - 横屏「代码演示」风格
 *
 * 核心亮点：
 * - IDE 编辑器风格代码区 + 逐字符打字动画
 * - 语法高亮（关键字、字符串、注释不同颜色）
 * - 实时终端输出区，带闪烁光标
 * - 注释气泡弹出解释关键行
 * - 文件标签 + 行号 + 光标闪烁
 *
 * 尺寸：1920x1080 横屏
 */

interface CodeLine {
  code: string;
  annotation?: string;
  isHighlight?: boolean;
}

interface OutputLine {
  text: string;
  type: 'success' | 'info' | 'error' | 'result';
}

interface SkillsCodeDemoProps {
  fileName: string;
  language: string;
  title: string;
  lines: CodeLine[];
  outputs: OutputLine[];
  summary: string;
}

const DEFAULT_PROPS: SkillsCodeDemoProps = {
  fileName: 'debounce.ts',
  language: 'TypeScript',
  title: '手写防抖函数 — 面试高频题',
  lines: [
    { code: 'function debounce(fn: Function, delay: number) {', annotation: '接收函数和延迟时间' },
    { code: '  let timer: ReturnType<typeof setTimeout>;' },
    { code: '' },
    { code: '  return function(...args: any[]) {', annotation: '返回新函数，用闭包保存 timer', isHighlight: true },
    { code: '    clearTimeout(timer);', annotation: '每次调用先清除上次计时器' },
    { code: '    timer = setTimeout(() => {', isHighlight: true },
    { code: '      fn.apply(this, args);', annotation: '延迟结束后才真正执行' },
    { code: '    }, delay);' },
    { code: '  };' },
    { code: '}' },
    { code: '' },
    { code: '// 使用示例' },
    { code: 'const search = debounce((q: string) => {' },
    { code: '  console.log("搜索:", q);' },
    { code: '}, 300);' },
  ],
  outputs: [
    { text: '$ ts-node debounce.ts', type: 'info' },
    { text: '> 快速输入: a, ab, abc', type: 'info' },
    { text: '> 等待 300ms...', type: 'info' },
    { text: '搜索: abc', type: 'success' },
    { text: '✅ 只执行了最后一次调用', type: 'result' },
  ],
  summary: '防抖核心：闭包 + clearTimeout + setTimeout，每次调用重置计时器',
};

const syntaxHighlight = (code: string): React.ReactNode => {
  const keywords = /\b(function|return|let|const|typeof|void|any|number|string|new)\b/g;
  const strings = /(["'`])(?:(?=(\\?))\2.)*?\1/g;
  const comments = /\/\/.*/g;

  const parts: { text: string; type: 'keyword' | 'string' | 'comment' | 'normal' }[] = [];
  let lastIndex = 0;
  const fullCode = code;

  const commentMatch = comments.exec(fullCode);
  if (commentMatch) {
    if (commentMatch.index > 0) {
      parts.push({ text: fullCode.substring(0, commentMatch.index), type: 'normal' });
    }
    parts.push({ text: commentMatch[0], type: 'comment' });
    return parts.map((p, i) => (
      <span key={i} style={{ color: p.type === 'comment' ? '#6a9955' : '#d4d4d4' }}>{p.text}</span>
    ));
  }

  const tokens: { start: number; end: number; type: 'keyword' | 'string' }[] = [];

  let m;
  const kw = new RegExp(keywords);
  while ((m = kw.exec(fullCode)) !== null) {
    tokens.push({ start: m.index, end: m.index + m[0].length, type: 'keyword' });
  }

  const st = new RegExp(strings);
  while ((m = st.exec(fullCode)) !== null) {
    tokens.push({ start: m.index, end: m.index + m[0].length, type: 'string' });
  }

  tokens.sort((a, b) => a.start - b.start);

  const result: React.ReactNode[] = [];
  let pos = 0;
  for (const token of tokens) {
    if (token.start < pos) continue;
    if (token.start > pos) {
      result.push(<span key={`n${pos}`} style={{ color: '#d4d4d4' }}>{fullCode.substring(pos, token.start)}</span>);
    }
    const color = token.type === 'keyword' ? '#569cd6' : '#ce9178';
    result.push(<span key={`t${token.start}`} style={{ color }}>{fullCode.substring(token.start, token.end)}</span>);
    pos = token.end;
  }
  if (pos < fullCode.length) {
    result.push(<span key={`e${pos}`} style={{ color: '#d4d4d4' }}>{fullCode.substring(pos)}</span>);
  }

  return result.length > 0 ? result : <span style={{ color: '#d4d4d4' }}>{fullCode}</span>;
};

export const SkillsCodeDemo: React.FC<Partial<SkillsCodeDemoProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Typing speed: ~2 chars per frame
  const typingSpeed = 2;
  const totalChars = p.lines.reduce((sum, l) => sum + l.code.length + 1, 0);
  const typedChars = Math.floor(interpolate(frame, [30, 30 + totalChars / typingSpeed], [0, totalChars], { extrapolateRight: 'clamp' }));

  // Figure out which line and how many chars
  let charCount = 0;
  let currentLineIndex = 0;
  let currentLineChars = 0;
  for (let i = 0; i < p.lines.length; i++) {
    const lineLen = p.lines[i].code.length + 1;
    if (charCount + lineLen > typedChars) {
      currentLineIndex = i;
      currentLineChars = typedChars - charCount;
      break;
    }
    charCount += lineLen;
    currentLineIndex = i + 1;
    currentLineChars = 0;
  }

  // Output phase
  const outputStart = 30 + totalChars / typingSpeed + 20;
  const outputLines = Math.floor(
    interpolate(frame, [outputStart, outputStart + p.outputs.length * 20], [0, p.outputs.length], { extrapolateRight: 'clamp' })
  );

  // Summary
  const summaryStart = outputStart + p.outputs.length * 20 + 20;
  const summaryOpacity = interpolate(frame, [summaryStart, summaryStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Header
  const headerProgress = spring({ frame, fps, config: { damping: 18, stiffness: 100 } });

  // Cursor blink
  const cursorVisible = frame % 20 < 12;

  return (
    <AbsoluteFill
      style={{
        background: '#1e1e1e',
        fontFamily: '"SF Mono", "Fira Code", "Cascadia Code", Consolas, monospace',
        color: '#d4d4d4',
        overflow: 'hidden',
      }}
    >
      {/* Title bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 84,
          background: '#181818',
          borderBottom: '1px solid #2d2d2d',
          display: 'flex',
          alignItems: 'center',
          padding: '0 32px',
          justifyContent: 'space-between',
          opacity: headerProgress,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          {/* Traffic lights */}
          <div style={{ display: 'flex', gap: 10 }}>
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#ff5f56' }} />
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#ffbd2e' }} />
            <div style={{ width: 14, height: 14, borderRadius: '50%', background: '#27c93f' }} />
          </div>
          <span style={{ fontSize: 24, color: 'rgba(255,255,255,0.5)', marginLeft: 14 }}>{p.title}</span>
        </div>
        {/* File tab */}
        <div
          style={{
            padding: '8px 20px',
            background: '#1e1e1e',
            borderRadius: '8px 8px 0 0',
            fontSize: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            color: '#cccccc',
          }}
        >
          <span style={{ color: '#519aba' }}>TS</span>
          {p.fileName}
        </div>
      </div>

      {/* ========== CODE EDITOR AREA ========== */}
      <div
        style={{
          position: 'absolute',
          top: 84,
          left: 0,
          right: '40%',
          bottom: 0,
          display: 'flex',
        }}
      >
        {/* Line numbers */}
        <div
          style={{
            width: 76,
            padding: '20px 0',
            background: '#1e1e1e',
            borderRight: '1px solid #2d2d2d',
            textAlign: 'right',
            paddingRight: 16,
          }}
        >
          {p.lines.map((_, i) => (
            <div
              key={i}
              style={{
                height: 44,
                fontSize: 22,
                lineHeight: '44px',
                color: i === currentLineIndex ? '#c6c6c6' : '#858585',
              }}
            >
              {i + 1}
            </div>
          ))}
        </div>

        {/* Code content */}
        <div style={{ flex: 1, padding: '20px 24px', position: 'relative', overflow: 'hidden' }}>
          {p.lines.map((line, i) => {
            const isFullyTyped = i < currentLineIndex;
            const isCurrentLine = i === currentLineIndex;
            const isUntyped = i > currentLineIndex;

            let displayCode = '';
            if (isFullyTyped) displayCode = line.code;
            else if (isCurrentLine) displayCode = line.code.substring(0, currentLineChars);
            else displayCode = '';

            return (
              <div
                key={i}
                style={{
                  height: 44,
                  lineHeight: '44px',
                  fontSize: 24,
                  whiteSpace: 'pre',
                  position: 'relative',
                  background: (isCurrentLine || line.isHighlight) ? 'rgba(255,255,255,0.04)' : 'transparent',
                  borderLeft: isCurrentLine ? '2px solid #569cd6' : '2px solid transparent',
                  paddingLeft: 8,
                  opacity: isUntyped ? 0 : 1,
                }}
              >
                {syntaxHighlight(displayCode)}
                {isCurrentLine && cursorVisible && (
                  <span
                    style={{
                      display: 'inline-block',
                      width: 3,
                      height: 26,
                      background: '#aeafad',
                      verticalAlign: 'middle',
                      marginLeft: 1,
                    }}
                  />
                )}
              </div>
            );
          })}

          {/* Annotation bubbles */}
          {p.lines.map((line, i) => {
            if (!line.annotation || i > currentLineIndex) return null;
            const bubbleOpacity = interpolate(frame, [30 + i * 8, 30 + i * 8 + 15], [0, 1], { extrapolateRight: 'clamp' });
            if (bubbleOpacity < 0.05) return null;

            return (
              <div
                key={`ann-${i}`}
                style={{
                  position: 'absolute',
                  top: i * 44 + 6,
                  right: 24,
                  padding: '6px 16px',
                  background: 'rgba(86,156,214,0.12)',
                  border: '1px solid rgba(86,156,214,0.25)',
                  borderRadius: 8,
                  fontSize: 18,
                  color: '#9cdcfe',
                  opacity: bubbleOpacity,
                  whiteSpace: 'nowrap',
                  fontFamily: '-apple-system, "PingFang SC", sans-serif',
                }}
              >
                💬 {line.annotation}
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== TERMINAL OUTPUT AREA ========== */}
      <div
        style={{
          position: 'absolute',
          top: 84,
          right: 0,
          width: '40%',
          bottom: 0,
          borderLeft: '1px solid #2d2d2d',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Terminal header */}
        <div
          style={{
            padding: '12px 24px',
            background: '#181818',
            borderBottom: '1px solid #2d2d2d',
            fontSize: 18,
            color: '#858585',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: '#27c93f' }}>●</span>
          TERMINAL
        </div>

        {/* Output content */}
        <div style={{ padding: '20px 24px', flex: 1, fontFamily: '"SF Mono", "Fira Code", monospace' }}>
          {p.outputs.slice(0, outputLines).map((out, i) => {
            const colorMap = {
              success: '#4ec9b0',
              info: '#858585',
              error: '#f14c4c',
              result: '#dcdcaa',
            };

            return (
              <div
                key={i}
                style={{
                  fontSize: 22,
                  lineHeight: '42px',
                  color: colorMap[out.type],
                  opacity: interpolate(frame, [outputStart + i * 20, outputStart + i * 20 + 10], [0, 1], { extrapolateRight: 'clamp' }),
                }}
              >
                {out.text}
              </div>
            );
          })}

          {/* Blinking cursor at terminal */}
          {frame >= outputStart && (
            <span
              style={{
                display: 'inline-block',
                width: 12,
                height: 26,
                background: '#aeafad',
                opacity: cursorVisible ? 0.7 : 0,
                verticalAlign: 'middle',
                marginTop: 8,
              }}
            />
          )}
        </div>

        {/* Summary */}
        <div
          style={{
            padding: '20px 24px',
            background: '#181818',
            borderTop: '1px solid #2d2d2d',
            opacity: summaryOpacity,
            fontFamily: '-apple-system, "PingFang SC", sans-serif',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span style={{ fontSize: 28 }}>💡</span>
            <span style={{ fontSize: 24, color: '#dcdcaa', lineHeight: 1.5 }}>{p.summary}</span>
          </div>
        </div>
      </div>

      {/* Language badge */}
      <div
        style={{
          position: 'absolute',
          bottom: 16,
          left: 16,
          padding: '8px 18px',
          background: '#007acc',
          borderRadius: 6,
          fontSize: 18,
          fontWeight: 700,
          color: 'white',
        }}
      >
        {p.language}
      </div>

      {/* Progress */}
      <div
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          width: `${interpolate(frame, [0, summaryStart + 40], [0, 100], { extrapolateRight: 'clamp' })}%`,
          height: 3,
          background: '#007acc',
        }}
      />
    </AbsoluteFill>
  );
};
